import * as React from 'react';
import ElementsWrapperRender from './ElementsWrapperRender';
import IframeEventsManager from '../services/frames/IframeEventsManager';
import WebsitePreviewCreator from '../services/WebsitePreviewCreator';
import { observer } from 'mobx-react-lite';
import SiteBuilderModel from '../model/SiteBuilderModel';
import { Spin } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Trans } from 'react-i18next';
import Preview from './components/Preview';
import { MOBILE, TABLET } from '../consts/devices';
import PreviewTour from './components/PreviewTour';
import SlideLoaderIndicator from './components/SlideLoaderIndicator';
import configEvents from '../config/config-events';
import BusinessModel from '../../FindYourBusiness/models/BusinessModel';
import HelpBoxCompanionModel from '../../../models/HelpBoxCompanionModel';
import Sidebar from './components/Sidebar';
import MobileTopNav from './components/MobileTopNav';
import PagesTree from './components/PagesTree';
import AbjustColors from './components/AbjustColors';
import { Layout } from 'antd';
import PreviewHeader from './components/PreviewHeader';
import SidebarActions from './components/SidebarActions';
import PreviewCTA from '../../../components/buttons/PreviewCTA';
import LogosPanel from './components/LogosPanel';
import FontSelector from '../../../components/FontSelector';
import { SmallText } from '../../../components/ui/Typography';
import Api from '../../../services/Api';

interface FCProps {}

const Index: React.FC<FCProps> = observer(({}) => {
  const [purchaseLoading, setPurchaseLoading] = React.useState(false);
  const iframeRef = React.useRef(null);

  const iframeWidth =
    SiteBuilderModel.currentSelectedDevice === MOBILE
      ? '375px'
      : SiteBuilderModel.currentSelectedDevice === TABLET
        ? '768px'
        : '100%';

  const normalizeUrl = (url: string) => {
    if (!/^(https?:\/\/)/i.test(url)) {
      url = 'https://' + url;
    }
    return new URL(url);
  };

  React.useEffect(() => {
    IframeEventsManager.initEvents();
    configEvents();

    HelpBoxCompanionModel.setDisplayHelpBox(false);

    return () => {
      IframeEventsManager.clearEvents();
    };
  }, []);

  const [drawerKey, setDrawerKey] = React.useState<string | null>(null);
  const drawerWidth = 350;
  const resetColorsRef = React.useRef<(() => void) | null>(null);

  const handleSidebarSelect = (key: string) => {
    setDrawerKey((prev) => (prev === key ? null : key));
  };

  const handleCloseSidebar = () => {
    setDrawerKey(null);
  };

  const handlePreviewSaveAndContinue = async () => {
    try {
      setPurchaseLoading(true);

      SiteBuilderModel.siteBuilderWizardData.setFormData({
        colorMap: SiteBuilderModel.finalColorScheme
      });

      const sessionResult = await WebsitePreviewCreator.goToPurchase();

      setPurchaseLoading(false);

      const url = new URL(Api.addUtmParametersToUrl(sessionResult.data.redirect_url));

      url.searchParams.append(
        'setup_identifier',
        sessionResult.data.setup_identifier
      );

      BusinessModel.businessWizardData.google_place_id &&
        url.searchParams.append(
          'google_place_id',
          BusinessModel.businessWizardData.google_place_id
        );

      url.searchParams.append(
        'business_main_category',
        BusinessModel.getWizardData().category
      );

      BusinessModel.getWizardData().company_name &&
        url.searchParams.append(
          'company_name',
          BusinessModel.getWizardData().company_name
        );

      url.searchParams.append(
        'domain',
        normalizeUrl(BusinessModel.getWizardData().website).hostname
      );

      console.log(url.toString());
      window.location.href = url.toString();
    } catch (err) {
      console.error('Purchase redirect failed:', err);
      setPurchaseLoading(false);
    }
  };

  const renderDrawerPanelContent = () => {
    if (!drawerKey) return null;

    if (drawerKey === 'colors') {
      return (
        <div className="p-[24px] max-md:p-4">
          <AbjustColors onResetRef={resetColorsRef} />
          <SidebarActions
            onReset={() => {
              if (resetColorsRef.current) {
                resetColorsRef.current();
              }
            }}
            onClose={handleCloseSidebar}
          />
        </div>
      );
    }

    if (drawerKey === 'logo') {
      return (
        <div className="p-[24px] max-md:p-4">
          <LogosPanel onClose={handleCloseSidebar} />
          <SidebarActions onClose={handleCloseSidebar} showReset={false} />
        </div>
      );
    }

    if (drawerKey === 'fonts') {
      return (
        <div className="p-[24px] max-md:p-4">
          <FontSelector />
          <SidebarActions onClose={handleCloseSidebar} showReset={false} />
          <div className="flex gap-[10px] mt-[24px]">
            <InfoCircleOutlined
              style={{ width: 16, height: 16, fontSize: 16 }}
            />
            <SmallText>
              <Trans
                i18nKey="fontSelector.recommendation"
                defaults="These font combinations are recommendations for your website. You can use any fonts you like in the CMS at any time after purchasing the site."
              />
            </SmallText>
          </div>
        </div>
      );
    }

    if (drawerKey === 'pages') {
      return (
        <div>
          <PagesTree />
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <div
        className={
          SiteBuilderModel.iframeVisibility
            ? 'preview-editor-shell w-full h-full flex-1'
            : 'hidden'
        }>
        <Layout>
          <Layout.Header
            className="preview-editor-header hidden md:flex"
            style={{
              background: '#fff',
              borderBottom: '1px solid rgba(0,0,0,0.15)',
              padding: 0,
              height: 'auto',
              display: 'flex',
              position: 'sticky',
              top: 0,
              zIndex: 100,
              width: '100%'
            }}>
            <PreviewHeader />
          </Layout.Header>

          <MobileTopNav
            selectedKey={drawerKey}
            onSelect={handleSidebarSelect}
          />

          <div className="flex flex-col md:flex-row h-[calc(100vh-224px)] max-md:pt-[calc(var(--preview-mobile-chrome-top)-20px)] max-md:h-[calc(100vh-var(--preview-mobile-chrome-bottom))]">
            <div className="hidden md:block h-full shrink-0">
              <Sidebar selectedKey={drawerKey} onSelect={handleSidebarSelect} />
            </div>

            {drawerKey && (
              <>
                <button
                  type="button"
                  className="md:hidden fixed left-0 right-0 bottom-0 z-[104] border-0 cursor-default"
                  style={{
                    top: 'var(--preview-mobile-chrome-top)',
                    background: 'rgba(0, 0, 0, 0.2)'
                  }}
                  aria-label="Close panel"
                  onClick={handleCloseSidebar}
                />
                <div className="preview-drawer-panel">
                  {renderDrawerPanelContent()}
                </div>
              </>
            )}

            <div
              className="flex-1 min-w-0 h-full"
              style={{
                transition: 'margin-left 0.2s'
              }}>
              <div style={{ background: '#f7fafc', minHeight: '100%' }}>
                <SlideLoaderIndicator />
                <PreviewTour />
                {SiteBuilderModel.iframeLoaded === false && (
                  <div className="flex w-full items-center justify-center h-[200px]">
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                      }
                    />
                  </div>
                )}
                <div
                  className="flex w-full flex-1 mx-auto my-0 gap-6"
                  style={{
                    justifyContent: 'center'
                  }}>
                  <div className="flex w-full flex-col min-h-[1100px] max-md:min-h-0 p-3 md:p-[24px] mx-auto transition-all duration-200 max-w-[1620px]">
                    {SiteBuilderModel.iframeLoaded && (
                      <ElementsWrapperRender iframeRef={iframeRef} />
                    )}
                    <Preview ref={iframeRef} width={iframeWidth} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Layout>

        <PreviewCTA onBuy={handlePreviewSaveAndContinue} />
      </div>
    </>
  );
});

export default Index;
